import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { RootState } from '@/store';
import { signupUser, clearError } from '@/store/slices/authSlice';
import { ROUTES } from '@/utils/constants';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { fadeInUp } from '@/utils/animations';

const SignupPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
    terms: false,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  // Clear any existing errors when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (formData.username.length < 3) errors.username = 'Username must be at least 3 characters';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.email.includes('@')) errors.email = 'Invalid email address';
    if (!formData.password.trim()) errors.password = 'Password is required';
    if (formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) errors.confirmPassword = "Passwords don't match";
    if (!formData.terms) errors.terms = 'You must agree to the terms and conditions';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    
    if (validateForm()) {
      try {
        // Remove terms field before sending to API
        const { terms, ...signupData } = formData;
        console.log('Sending to API:', signupData);
        await dispatch(signupUser(signupData as any)).unwrap();
        
        // Preserve pending action and redirect path for after login
        // (pending action will be executed after user logs in)
        navigate(ROUTES.LOGIN, { 
          state: { 
            message: 'Account created successfully! Please login to continue.',
            preservePendingAction: true 
          } 
        });
      } catch (error) {
        console.error('Signup error:', error);
        // Error is handled by the auth slice
      }
    }
  };

  return (
    <motion.div initial="initial" animate="animate" variants={fadeInUp}>
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create your account</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <Link
            to={ROUTES.LOGIN}
            className="font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            Sign in
          </Link>
        </p>
      </div>

      {/* Form */}
      <form className="space-y-6" onSubmit={onSubmit} noValidate>
        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
            <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
          </div>
        )}

        {/* First Name & Last Name */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              First name
            </label>
            <input
              id="first_name"
              type="text"
              name="first_name"
              autoComplete="given-name"
              value={formData.first_name}
              onChange={handleInputChange}
              className={cn(
                'w-full h-12 px-4 rounded-lg',
                'bg-gray-50 dark:bg-dark-700',
                'border-2 transition-colors',
                formErrors.first_name
                  ? 'border-error-500 focus:border-error-500'
                  : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
                'text-gray-900 dark:text-white placeholder-gray-500',
                'focus:outline-none focus:ring-0'
              )}
              placeholder="Enter your first name"
            />
            {formErrors.first_name && (
              <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.first_name}</p>
            )}
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Last name
            </label>
            <input
              id="last_name"
              type="text"
              name="last_name"
              autoComplete="family-name"
              value={formData.last_name}
              onChange={handleInputChange}
              className={cn(
                'w-full h-12 px-4 rounded-lg',
                'bg-gray-50 dark:bg-dark-700',
                'border-2 transition-colors',
                formErrors.last_name
                  ? 'border-error-500 focus:border-error-500'
                  : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
                'text-gray-900 dark:text-white placeholder-gray-500',
                'focus:outline-none focus:ring-0'
              )}
              placeholder="Enter your last name"
            />
            {formErrors.last_name && (
              <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.last_name}</p>
            )}
          </div>
        </div>

        {/* Username */}
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Username
          </label>
          <input
            id="username"
            type="text"
            name="username"
            autoComplete="username"
            value={formData.username}
            onChange={handleInputChange}
            className={cn(
              'w-full h-12 px-4 rounded-lg',
              'bg-gray-50 dark:bg-dark-700',
              'border-2 transition-colors',
              formErrors.username
                ? 'border-error-500 focus:border-error-500'
                : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
              'text-gray-900 dark:text-white placeholder-gray-500',
              'focus:outline-none focus:ring-0'
            )}
            placeholder="Choose a username"
          />
          {formErrors.username && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.username}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Email address
          </label>
          <input
            id="email"
            type="email"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleInputChange}
            className={cn(
              'w-full h-12 px-4 rounded-lg',
              'bg-gray-50 dark:bg-dark-700',
              'border-2 transition-colors',
              formErrors.email
                ? 'border-error-500 focus:border-error-500'
                : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
              'text-gray-900 dark:text-white placeholder-gray-500',
              'focus:outline-none focus:ring-0'
            )}
            placeholder="name@example.com"
          />
          {formErrors.email && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Phone number <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="phone"
            type="tel"
            name="phone"
            autoComplete="tel"
            value={formData.phone}
            onChange={handleInputChange}
            className={cn(
              'w-full h-12 px-4 rounded-lg',
              'bg-gray-50 dark:bg-dark-700',
              'border-2 transition-colors',
              formErrors.phone
                ? 'border-error-500 focus:border-error-500'
                : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
              'text-gray-900 dark:text-white placeholder-gray-500',
              'focus:outline-none focus:ring-0'
            )}
            placeholder="Enter your phone number"
          />
          {formErrors.phone && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.phone}</p>
          )}
        </div>

        {/* Address */}
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Address <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="address"
            type="text"
            name="address"
            autoComplete="street-address"
            value={formData.address}
            onChange={handleInputChange}
            className={cn(
              'w-full h-12 px-4 rounded-lg',
              'bg-gray-50 dark:bg-dark-700',
              'border-2 transition-colors',
              formErrors.address
                ? 'border-error-500 focus:border-error-500'
                : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
              'text-gray-900 dark:text-white placeholder-gray-500',
              'focus:outline-none focus:ring-0'
            )}
            placeholder="Enter your address"
          />
          {formErrors.address && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.address}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleInputChange}
              className={cn(
                'w-full h-12 px-4 pr-12 rounded-lg',
                'bg-gray-50 dark:bg-dark-700',
                'border-2 transition-colors',
                formErrors.password
                  ? 'border-error-500 focus:border-error-500'
                  : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
                'text-gray-900 dark:text-white placeholder-gray-500',
                'focus:outline-none focus:ring-0'
              )}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
          {formErrors.password && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={cn(
                'w-full h-12 px-4 pr-12 rounded-lg',
                'bg-gray-50 dark:bg-dark-700',
                'border-2 transition-colors',
                formErrors.confirmPassword
                  ? 'border-error-500 focus:border-error-500'
                  : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
                'text-gray-900 dark:text-white placeholder-gray-500',
                'focus:outline-none focus:ring-0'
              )}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
          {formErrors.confirmPassword && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.confirmPassword}</p>
          )}
        </div>

        {/* Terms & Conditions */}
        <div>
          <div className="flex items-start">
            <input
              id="terms"
              type="checkbox"
              name="terms"
              checked={formData.terms}
              onChange={handleInputChange}
              className="h-4 w-4 mt-0.5 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="terms" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              I agree to the{' '}
              <a href="#" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                Privacy Policy
              </a>
            </label>
          </div>
          {formErrors.terms && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.terms}</p>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          size="lg"
          variant="primary"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          rightIcon={<ArrowRightIcon className="h-5 w-5" />}
        >
          {isLoading ? 'Creating account...' : 'Create account'}
        </Button>
      </form>

      {/* Divider */}
      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-dark-600" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-dark-800 text-gray-500 dark:text-gray-400">
            Already have an account?
          </span>
        </div>
      </div>

      {/* Sign In Link */}
      <Button variant="outline" size="lg" fullWidth onClick={() => navigate(ROUTES.LOGIN)}>
        Sign in instead
      </Button>
    </motion.div>
  );
};

export default SignupPage;
